import { useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdvanceLockBanner,
  AdvanceLowBalanceAlert,
  AdvanceCreditHero,
  AdvanceStatCards,
  AdvanceBatchesTable,
  FfcTransfersList,
  AdvancePreActivationCard,
  AdvanceLoadError,
  AdvanceRejectedCard,
  AdvanceSuspendedCard,
  AdvancePaymentMethodBanner,
  AdvanceCodPromoCard,
} from '@/components/AdvanceCod';
import LedgerTab from '@/pages/advance-cod/ledger';
import { ROUTE } from '@/config';
import { TicketEntityIDFieldEnum } from '@/constants';
import { useOpenEntityTicketModal } from '@/hooks';
import usePermission from '@/hooks/usePermission';
import {
  useFetchAdvanceBatches,
  useFetchAdvanceCodSummary,
  useFetchAdvanceCodTransfers,
} from '@/hooksApi';
import { AdvanceBatch, PermissionName } from '@/types';

const PAGE_SIZE = 25;

const num = (value: string | number | null | undefined): number => Number(value ?? 0);

type AdvanceSection = 'overview' | 'ledger';

/** Secondary Overview | Ledger switcher (deep-linkable via ?section=ledger). */
function SectionSwitcher({
  section,
  onChange,
}: {
  section: AdvanceSection;
  onChange: (section: AdvanceSection) => void;
}) {
  const { t } = useTranslation();
  const items: Array<{ key: AdvanceSection; label: string }> = [
    { key: 'overview', label: String(t('advance_cod_overview')) },
    { key: 'ledger', label: String(t('advance_cod_ledger')) },
  ];
  return (
    <div
      className='mb-4 inline-flex items-center gap-1 rounded-xl bg-[rgb(245,245,243)] p-0.5'
      data-testid='advance-cod-section-switcher'
    >
      {items.map((item) => {
        const active = section === item.key;
        return (
          <button
            key={item.key}
            type='button'
            onClick={() => onChange(item.key)}
            className={`cursor-pointer rounded-lg border-0 px-4 py-1.5 text-[12.5px] font-semibold transition-all ${
              active
                ? 'bg-white text-[rgb(57,57,60)] shadow-sm'
                : 'bg-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Advance COD tab — the approved v2 design (DESIGN-v2-inventory.md). Lives
 * inside the Finance page (W9). State routing off the summary financing state
 * (task D): suspended → distinct suspended card; enrolled-but-not-FFC-active or
 * a pending request → State B pre-activation card; no financing + no request →
 * promo; active → State A. State A carries an Overview | Ledger section switcher
 * (?section=ledger), a payment-method prompt, lock banner, optional low-balance
 * alert, dark credit hero, stat cards, batches table, and transfers list. Data
 * arrives with tolerant fallbacks (W6) — missing fields render 0 / hidden.
 */
function AdvanceCodTab() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [page, setPage] = useState(1);
  const [transfersPage, setTransfersPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();

  const section: AdvanceSection = searchParams.get('section') === 'ledger' ? 'ledger' : 'overview';
  const setSection = (next: AdvanceSection) => {
    // Preserve the `tab` param (this tab lives inside Finance) — only toggle
    // `section`, dropped entirely for the default Overview so the URL stays clean.
    const params = new URLSearchParams(searchParams);
    if (next === 'ledger') params.set('section', 'ledger');
    else params.delete('section');
    setSearchParams(params);
  };

  const { data: summaryData, isLoading, isError, refetch } = useFetchAdvanceCodSummary();
  const summary = summaryData?.object;

  // Activated = FFC has assigned a credit limit. Fallback to `activated_at`
  // while the summary v2 `ffc_activated` flag lands server-side.
  const ffcActivated = summary?.ffc_activated ?? !!summary?.activated_at;

  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.REMITTANCE);
  const canTicket = hasPermission(PermissionName.CREATE_TICKET);

  const { data: batchesData, isFetching: batchesFetching } = useFetchAdvanceBatches(
    page,
    PAGE_SIZE,
    undefined,
    ffcActivated,
  );
  const batches = batchesData?.object?.items ?? [];
  const batchesTotal = batchesData?.object?.total ?? 0;

  const { data: transfersData, isFetching: transfersFetching } = useFetchAdvanceCodTransfers(
    transfersPage,
    PAGE_SIZE,
    ffcActivated,
  );
  const transfers = transfersData?.object?.items ?? [];
  const transfersTotal = transfersData?.object?.total ?? 0;

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent' />
      </div>
    );
  }

  // A transient summary failure (network blip, 500, or a 403 for a
  // permission-less sub-user) must NOT fall through to a lower state and show
  // an activated merchant false onboarding guidance. Render a retryable error
  // state instead; only reach the real states once the summary loaded.
  if (isError || !summary) {
    return <AdvanceLoadError onRetry={refetch} />;
  }

  // ── State routing (task D) ──────────────────────────────────────────────
  // Prefer the server-derived `financing_status` (FfcSummaryService
  // ::deriveFinancingStatus) — the field emitted expressly for panel selection.
  // It already collapses financing row + open-request state into one value, so
  // routing off it matches the backend exactly and avoids client-side
  // divergences (e.g. an FFC-active row with the Boxy toggle off, which is
  // pending_activation server-side but would look "active" to a naive
  // is_enabled/ffc_activated derivation). The client-side derivation below stays
  // as a fallback for a stale backend that omits the field; `rejected` (a
  // permanent decline with no dedicated panel yet) also falls through to it.
  const financingStatus = summary.financing_status;

  if (financingStatus === 'suspended') {
    return <AdvanceSuspendedCard />;
  }
  // Permanent FFC decline — must NOT fall through to the pre-activation or
  // promo cards (a declined merchant would be told activation is underway).
  if (financingStatus === 'rejected') {
    return <AdvanceRejectedCard />;
  }
  if (financingStatus === 'pending_activation') {
    return <AdvancePreActivationCard />;
  }
  if (financingStatus === 'not_enrolled') {
    return <AdvanceCodPromoCard />;
  }

  if (financingStatus !== 'active') {
    // No server-derived status (stale backend), or an unmapped value such as
    // `rejected` → fall back to the client-side derivation.
    // Suspended is checked first: a suspended program is not ffc_activated, so
    // it would otherwise fall into the pre-activation card and show branch steps
    // that don't apply. Distinct red/amber suspended card instead.
    if (summary.status === 'suspended') {
      return <AdvanceSuspendedCard />;
    }

    if (!ffcActivated) {
      // Enrolled (is_enabled) or a request already in flight → pre-activation
      // (State B). Otherwise (no financing, no request) → the promo CTA.
      if (summary.is_enabled || summary.request_status) {
        return <AdvancePreActivationCard />;
      }
      return <AdvanceCodPromoCard />;
    }
  }
  // financing_status === 'active', or a stale backend whose client-side
  // derivation resolved to an activated merchant → State A below.

  // ── State A values (tolerant fallbacks to the v1 summary fields) ──
  const available = num(summary.remaining_eligible_balance ?? summary.available_credit_limit);
  const totalLoan = num(summary.total_loan ?? summary.assigned_credit_limit);
  const maxCashout = num(summary.max_shipment_payout ?? summary.max_single_cashout_amount);
  const inProgress = num(summary.in_progress_value);
  const returned = num(summary.returned_value);
  const delivered = num(summary.delivered_toward_threshold);
  const threshold = num(summary.repayment_threshold);
  const scheduledNet = num(summary.scheduled_pickup_net_value);

  const showAlert = summary.low_balance_alert ?? (scheduledNet > 0 && scheduledNet > available);

  // Batch tickets reuse the PAYMENT ticket form (there is no dedicated FFC
  // batch form upstream), but the entity platform-code field is relabeled so
  // the modal reads "Batch Platform Code" instead of "Remittance Platform Code".
  const onTicket = (batch: AdvanceBatch) =>
    openEntityTicketModal(batch.platform_code, batch.uid, 'advance_cod_batch_platform_code');
  const onRowClick = (batch: AdvanceBatch) =>
    navigate(generatePath(ROUTE.ADVANCE_COD_BATCH_DETAILS, { uid: batch.uid }));

  return (
    <div>
      {summary.payment_method_missing ? <AdvancePaymentMethodBanner /> : null}
      <SectionSwitcher section={section} onChange={setSection} />
      {section === 'ledger' ? (
        <LedgerTab />
      ) : (
        <>
          <AdvanceLockBanner />
          {showAlert ? (
            <AdvanceLowBalanceAlert scheduledNet={scheduledNet} available={available} />
          ) : null}
          <AdvanceCreditHero available={available} totalLoan={totalLoan} maxCashout={maxCashout} />
          <AdvanceStatCards
            inProgress={inProgress}
            returned={returned}
            delivered={delivered}
            threshold={threshold}
          />
          <AdvanceBatchesTable
            batches={batches}
            total={batchesTotal}
            page={page}
            perPage={PAGE_SIZE}
            loading={batchesFetching}
            canTicket={canTicket}
            onPageChange={setPage}
            onTicket={onTicket}
            onRowClick={onRowClick}
          />
          <FfcTransfersList
            transfers={transfers}
            total={transfersTotal}
            page={transfersPage}
            perPage={PAGE_SIZE}
            loading={transfersFetching}
            onPageChange={setTransfersPage}
          />
        </>
      )}
    </div>
  );
}

export default AdvanceCodTab;
